import React, { useMemo, useState } from 'react';
import { accountingApi } from '../../services/accountingApi';

const today = () => new Date().toISOString().slice(0, 10);
const emptyPayment = { payment_method_id: '', ruc_bank_id: '', amount: '', reference: '', note: '' };
const empty = {
    movement_date: today(),
    operation_type: 'EGRESO',
    sede_id: '',
    city: '',
    phone_number: '',
    amount: '',
    detail: '',
    payment_details: [{ ...emptyPayment }],
};

const money = (n) => Number(n || 0);
const cityFromSede = (sede) => String(sede?.sd_sede || sede?.sd_location || '').trim().split(/\s+/)[0] || '';

export default function MovementForm({ meta, onSaved }) {
    const [mode, setMode] = useState('NORMAL');
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const rucBanks = meta.rucBanks || [];
    const banksForRuc = useMemo(() => {
        if (!form.ruc_id) return rucBanks;
        return rucBanks.filter((x) => String(x.ruc_id) === String(form.ruc_id));
    }, [form.ruc_id, rucBanks]);

    const totals = useMemo(() => {
        const total = (form.payment_details || []).reduce((a, x) => a + money(x.amount), 0);
        const cash = (form.payment_details || []).reduce((a, x) => {
            const isCash = !x.payment_method_id && !x.ruc_bank_id;
            return a + (isCash ? money(x.amount) : 0);
        }, 0);
        const online = Math.max(total - cash, 0);
        return { total, cash, online };
    }, [form.payment_details, form.cash_amount]);

    const change = (name, value) => {
        const patch = { [name]: value };
        if (name === 'sede_id') {
            const sede = (meta.sedes || []).find((x) => String(x.sd_id || x.id) === String(value));
            patch.city = cityFromSede(sede);
        }
        if (name === 'ruc_id') patch.bank_id = '';
        setForm((p) => ({ ...p, ...patch }));
    };

    const changePayment = (idx, name, value) => {
        setForm((p) => {
            const list = [...(p.payment_details || [])];
            list[idx] = { ...list[idx], [name]: value };
            return { ...p, payment_details: list };
        });
    };

    const addPayment = () => setForm((p) => ({ ...p, payment_details: [...(p.payment_details || []), { ...emptyPayment }] }));
    const removePayment = (idx) => setForm((p) => ({ ...p, payment_details: (p.payment_details || []).filter((_, i) => i !== idx) }));

    const confirmSave = () => window.confirm('Revise los datos antes de guardar. Si todo está correcto, confirme para registrar.');

    const payload = () => ({
        ...form,
        movement_type: mode,
        amount: mode === 'TRANSFER' ? form.amount : totals.total,
        cash_amount: totals.cash,
        online_amount: totals.online,
        payment_details: mode === 'NORMAL' ? (form.payment_details || []).filter((x) => money(x.amount) > 0) : [],
    });

    const submitNormal = async () => {
        if (!confirmSave()) return;
        setSaving(true);
        try {
            await accountingApi.createMovement(payload());
            setForm(empty);
            await onSaved();
            alert('Registro guardado correctamente.');
        } catch (e) {
            alert(e.response?.data?.message || 'No se pudo guardar el registro.');
        } finally {
            setSaving(false);
        }
    };

    const submitTransfer = async () => {
        if (!confirmSave()) return;
        setSaving(true);
        try {
            await accountingApi.createTransfer(payload());
            setForm(empty);
            await onSaved();
            alert('Transferencia registrada correctamente con egreso e ingreso vinculados.');
        } catch (e) {
            alert(e.response?.data?.message || 'No se pudo registrar la transferencia.');
        } finally {
            setSaving(false);
        }
    };

    const select = (name, label, rows, getLabel) => (
        <label>
            <span>{label}</span>
            <select value={form[name] || ''} onChange={(e) => change(name, e.target.value)}>
                <option value="">Seleccionar</option>
                {rows.map((x) => <option key={x.id || x.sd_id} value={x.id || x.sd_id}>{getLabel(x)}</option>)}
            </select>
        </label>
    );

    return (
        <section className="accCard movementForm accForm">
            <div className="accTabs">
                <button type="button" className={mode === 'NORMAL' ? 'active' : ''} onClick={() => setMode('NORMAL')}>Ingreso / Egreso</button>
                <button type="button" className={mode === 'TRANSFER' ? 'active' : ''} onClick={() => setMode('TRANSFER')}>Transferencia entre bancos</button>
            </div>

            <div className="movementGrid">
                <label><span>Fecha</span><input type="date" min={meta.minDate || '2026-07-03'} value={form.movement_date || ''} onChange={(e) => change('movement_date', e.target.value)} /></label>
                {select('sede_id', 'Sede', meta.sedes || [], (x) => `${x.sd_sede} - ${x.sd_location || ''}`)}
                <label><span>Ciudad</span><input value={form.city || ''} readOnly placeholder="Primera palabra de la sede" /></label>
                <label><span>Número de celular</span><input value={form.phone_number || ''} onChange={(e) => change('phone_number', e.target.value)} placeholder="Se coloca manualmente" /></label>

                {mode === 'NORMAL' && <>
                    <label><span>Tipo</span><select value={form.operation_type || 'EGRESO'} onChange={(e) => change('operation_type', e.target.value)}><option value="EGRESO">Egreso</option><option value="INGRESO">Ingreso</option></select></label>
                    {select('ruc_id', 'RUC empresa', meta.rucs || [], (x) => x.alias || x.business_name)}
                    {select('bank_id', 'Banco / cuenta principal', banksForRuc, (x) => x.label)}
                    {select('supplier_id', 'Proveedor', meta.suppliers || [], (x) => x.business_name)}
                </>}

                {mode === 'TRANSFER' && <>
                    {select('origin_ruc_bank_id', 'Banco origen', rucBanks, (x) => x.label)}
                    {select('destination_ruc_bank_id', 'Banco destino', rucBanks, (x) => x.label)}
                    <label><span>Monto transferencia</span><input type="number" step="0.01" value={form.amount || ''} onChange={(e) => change('amount', e.target.value)} /></label>
                </>}

                {select('area_id', 'Área', meta.areas || [], (x) => x.name)}
                {select('category_id', 'Categoría', meta.categories || [], (x) => `${x.operation_type} - ${x.name}`)}
                {select('requester_user_id', 'Solicitante interno', meta.users || [], (x) => `${x.name || ''} ${x.paternalSur || ''}`)}
                {select('requester_external_id', 'Solicitante externo', meta.externalRequesters || [], (x) => x.full_name)}
                {select('receipt_type_id', 'Comprobante', meta.receiptTypes || [], (x) => x.name)}
                <label><span>Número comprobante</span><input value={form.receipt_number || ''} onChange={(e) => change('receipt_number', e.target.value)} /></label>
                <label className="wide"><span>Detalle</span><textarea value={form.detail || ''} onChange={(e) => change('detail', e.target.value)} /></label>
            </div>

            {mode === 'NORMAL' && (
                <div className="paymentBox">
                    <div className="paymentHead">
                        <div>
                            <h3>Detalle de pago</h3>
                            <p>Permite pagar un mismo movimiento con efectivo y varios métodos online: banco de un RUC, Yape, transferencia, Culqi u otro método.</p>
                        </div>
                        <button type="button" onClick={addPayment}>+ Agregar método</button>
                    </div>

                    {(form.payment_details || []).map((p, idx) => (
                        <div className="paymentRow" key={idx}>
                            <label><span>Método</span><select value={p.payment_method_id || ''} onChange={(e) => changePayment(idx, 'payment_method_id', e.target.value)}><option value="">Efectivo / sin método</option>{(meta.paymentMethods || []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
                            <label><span>Banco / cuenta RUC</span><select value={p.ruc_bank_id || ''} onChange={(e) => changePayment(idx, 'ruc_bank_id', e.target.value)}><option value="">No aplica</option>{rucBanks.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</select></label>
                            <label><span>Monto</span><input type="number" step="0.01" value={p.amount || ''} onChange={(e) => changePayment(idx, 'amount', e.target.value)} /></label>
                            <label><span>Referencia</span><input value={p.reference || ''} onChange={(e) => changePayment(idx, 'reference', e.target.value)} /></label>
                            <button type="button" className="paymentRemove" onClick={() => removePayment(idx)} disabled={(form.payment_details || []).length === 1}>Quitar</button>
                        </div>
                    ))}

                    <div className="paymentTotals">
                        <span>Monto Efectivo: <b>S/ {totals.cash.toFixed(2)}</b></span>
                        <span>Monto Online: <b>S/ {totals.online.toFixed(2)}</b></span>
                        <span>Total Movimiento: <b>S/ {totals.total.toFixed(2)}</b></span>
                    </div>
                </div>
            )}

            <button className="accPrimary" type="button" disabled={saving} onClick={mode === 'TRANSFER' ? submitTransfer : submitNormal}>
                {mode === 'TRANSFER' ? 'Registrar transferencia' : 'Guardar registro'}
            </button>
        </section>
    );
}
